import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, GraduationCap, Calendar, TrendingUp, ArrowRight, MoreVertical, Target, Sparkles, Zap, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { communitiesApi, coursesApi, eventsApi, questsApi, matchingApi } from '../../api';
import { Button } from '../ui/button';
import DashboardLayout from './DashboardLayout';
import AuraScoreWidget from '../aura/AuraScoreWidget';

const StatCard = ({ icon: Icon, label, value, trend, color }) => (
  <div className="bg-white rounded-xl p-6 border border-gray-200">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      {trend && (
        <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
          <TrendingUp className="w-4 h-4" />
          {trend}
        </span>
      )}
    </div>
    <p className="text-3xl font-bold text-gray-900">{value}</p>
    <p className="text-gray-500 text-sm mt-1">{label}</p>
  </div>
);

const DashboardHome = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [communities, setCommunities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [quests, setQuests] = useState([]);
  const [connections, setConnections] = useState([]);
  const [stats, setStats] = useState({ members: 0, courses: 0, events: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [commRes, courseRes, eventRes, questRes, connRes] = await Promise.all([
          communitiesApi.list(0, 5),
          coursesApi.list(0, 5),
          eventsApi.list(0, 5),
          questsApi.getMyQuests().catch(() => ({ data: [] })),
          matchingApi.getSuggestions(3).catch(() => ({ data: [] })),
        ]);
        
        setCommunities(commRes.data || []);
        setCourses(courseRes.data || []);
        setEvents(eventRes.data || []);
        setQuests(questRes.data || []);
        setConnections(connRes.data || []);
        
        // Calculate stats
        const totalMembers = (commRes.data || []).reduce((sum, c) => sum + (c.members_count || 0), 0);
        setStats({
          members: totalMembers,
          courses: (courseRes.data || []).length,
          events: (eventRes.data || []).length,
        });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Welcome section */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.welcome', { name: user?.name?.split(' ')[0] || '' })}</h1>
        <p className="text-gray-500 mt-1">{t('dashboard.subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          label={t('dashboard.total_members')}
          value={stats.members.toLocaleString()}
          color="bg-emerald-500"
        />
        <StatCard
          icon={GraduationCap}
          label={t('dashboard.active_courses')}
          value={stats.courses}
          color="bg-teal-500"
        />
        <StatCard
          icon={Calendar}
          label={t('dashboard.upcoming_events')}
          value={stats.events}
          color="bg-green-500"
        />
        <StatCard
          icon={TrendingUp}
          label={t('dashboard.aura_score')}
          value={user?.aura_score ?? 0}
          color="bg-orange-500"
        />
      </div>

      {/* Content sections */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Communities */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.your_communities')}</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/communities')}>
              {t('dashboard.view_all')}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-4">
            {communities.length > 0 ? communities.slice(0, 3).map((community) => (
              <div key={community.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <img
                  src={community.image || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=100&q=80'}
                  alt={community.name}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{community.name}</p>
                  <p className="text-sm text-gray-500">{t('dashboard.members_count', { count: (community.members_count || 0).toLocaleString() })}</p>
                </div>
                <button className="p-2 text-gray-400 hover:text-gray-600">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            )) : (
              <p className="text-gray-500 text-center py-4">No communities yet</p>
            )}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.upcoming_events')}</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/events')}>
              {t('dashboard.view_all')}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-4">
            {events.length > 0 ? events.slice(0, 3).map((event) => (
              <div key={event.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 flex flex-col items-center justify-center">
                  <span className="text-xs text-emerald-600 font-medium">
                    {new Date(event.start_time).toLocaleDateString('en-US', { month: 'short' })}
                  </span>
                  <span className="text-lg font-bold text-emerald-600">
                    {new Date(event.start_time).getDate()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{event.title}</p>
                  <p className="text-sm text-gray-500">{t('dashboard.attending', { count: event.registrations_count || 0 })}</p>
                </div>
                {event.status === 'live' && (
                  <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                    {t('dashboard.live')}
                  </span>
                )}
              </div>
            )) : (
              <p className="text-gray-500 text-center py-4">No upcoming events</p>
            )}
          </div>
        </div>
      </div>

      {/* Courses section */}
      <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Available Courses</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/courses')}>
            View all courses
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {courses.length > 0 ? courses.slice(0, 3).map((course) => (
            <div key={course.id} className="group cursor-pointer">
              <div className="relative rounded-xl overflow-hidden mb-3">
                <img
                  src={course.image || 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=400&q=80'}
                  alt={course.title}
                  className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="font-medium text-gray-900 group-hover:text-emerald-600 transition-colors">{course.title}</h3>
              <p className="text-sm text-gray-500 mt-1">{course.instructor_name || 'Instructor'}</p>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-gray-500">{course.duration || 'N/A'}</span>
                <span className="text-sm font-semibold text-emerald-600">${course.price || 0}</span>
              </div>
            </div>
          )) : (
            <p className="text-gray-500 text-center py-4 col-span-3">No courses available</p>
          )}
        </div>
      </div>

      {/* Game Changer Features Section */}
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        {/* Aura Score Widget */}
        <AuraScoreWidget />

        {/* Active Quests Widget */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-500" />
              <h3 className="font-semibold text-gray-900">Active Quests</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/quests')}>
              View all
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-3">
            {quests.length > 0 ? quests.slice(0, 3).map((quest) => (
              <div key={quest.id || quest.quest_id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{quest.icon || '🎯'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{quest.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full"
                          style={{ width: `${((quest.progress || 0) / (quest.target || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{quest.progress || 0}/{quest.target || 1}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-medium text-emerald-600">+{quest.xp_reward || 50} XP</span>
                  </div>
                </div>
              </div>
            )) : (
              <p className="text-gray-500 text-center py-4">No active quests</p>
            )}
          </div>
        </div>

        {/* Connection Suggestions Widget */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-500" />
              <h3 className="font-semibold text-gray-900">Connections</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/connections')}>
              View all
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="space-y-3">
            {connections.length > 0 ? connections.map((suggestion) => (
              <div key={suggestion.id || suggestion.user_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <img
                  src={suggestion.avatar || suggestion.user?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80'}
                  alt={suggestion.name || suggestion.user?.name || 'User'}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{suggestion.name || suggestion.user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{suggestion.bio?.substring(0, 30) || 'Community member'}...</p>
                </div>
                <div className="flex items-center gap-1">
                  <Zap className="w-3 h-3 text-emerald-500" />
                  <span className="text-xs font-medium text-emerald-600">{Math.round(suggestion.match_score || 0)}%</span>
                </div>
              </div>
            )) : (
              <p className="text-gray-500 text-center py-4">No suggestions yet</p>
            )}
            <Button variant="outline" className="w-full mt-2" size="sm" onClick={() => navigate('/dashboard/connections')}>
              Find More Connections
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardHome;
